import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, UserPlus, RefreshCw, Ban, ShieldCheck } from "lucide-react";
import { api } from "@/lib/auth";
import { formatApiError } from "@/lib/clientAuth";

const sel = "rounded-lg border border-gold/20 bg-obsidian-900 px-3 py-2 text-xs text-ivory outline-none focus:border-gold/60";
// Administrators may only invite Dispatcher / Employee. The Primary Administrator is appointed by Nanak Chauffeurs.
const ROLES = [["corporate_dispatcher", "Dispatcher"], ["corporate_employee", "Employee"]];
const roleLabel = (r) => ({ corporate_admin: "Administrator", corporate_dispatcher: "Dispatcher", corporate_employee: "Employee" }[r] || r);
const STATUS_STYLE = { pending: "text-amber-400", accepted: "text-gold-light", expired: "text-ivory/40", revoked: "text-red-400" };

export default function TeamPanel({ companyId }) {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [invite, setInvite] = useState({ email: "", role: "corporate_dispatcher" });

  const load = useCallback(async () => {
    try {
      const [m, i] = await Promise.all([
        api.get(`/corporate/members?company_id=${companyId}`),
        api.get(`/corporate/invitations?company_id=${companyId}`),
      ]);
      setMembers(m.data.members);
      setInvitations(i.data.invitations);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    try {
      await api.post("/corporate/invite", { company_id: companyId, email: invite.email, role: invite.role });
      toast.success("Invitation sent (valid 72 hours)"); setInvite({ email: "", role: "corporate_dispatcher" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const resend = async (id) => { try { await api.post(`/corporate/invitations/${id}/resend`); toast.success("Invitation resent"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const revoke = async (id) => { try { await api.post(`/corporate/invitations/${id}/revoke`); toast.success("Invitation revoked"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const update = async (m, patch) => {
    try { await api.patch(`/corporate/members/${m.membership_id}`, patch); toast.success("Updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="mt-6 space-y-5" data-testid="team-panel">
      <div className="rounded-2xl border border-gold/15 bg-obsidian-800 p-5">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold"><UserPlus className="h-3.5 w-3.5" /> Invite Dispatcher / Employee</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input data-testid="invite-email" type="email" placeholder="work.email@company.com" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className={`${sel} flex-1`} />
          <select data-testid="invite-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className={sel}>
            {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button data-testid="invite-submit" onClick={sendInvite} disabled={!invite.email} className="inline-flex items-center gap-1.5 rounded-full bg-gold px-5 py-2 text-xs font-semibold text-obsidian-900 disabled:opacity-40"><Mail className="h-3.5 w-3.5" /> Send</button>
        </div>
        <p className="mt-2 text-xs text-ivory/45">Each person gets their own secure, single-use invitation valid for 72 hours. Shared logins are not permitted.</p>
      </div>

      {invitations.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-gold/15 bg-obsidian-800" data-testid="invitations-list">
          <p className="border-b border-gold/10 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-gold">Invitations</p>
          {invitations.map((inv) => (
            <div key={inv.invite_id} className="flex flex-wrap items-center justify-between gap-3 border-b border-gold/5 px-5 py-3" data-testid={`invitation-${inv.email}`}>
              <div>
                <p className="text-sm text-ivory">{inv.email} <span className="text-ivory/40">· {roleLabel(inv.role)}{inv.is_primary ? " (Primary)" : ""}</span></p>
                <p className="text-xs"><span className={STATUS_STYLE[inv.status]} data-testid={`invitation-status-${inv.email}`}>{inv.status}</span></p>
              </div>
              {inv.status === "pending" && (
                <div className="flex gap-2">
                  <button onClick={() => resend(inv.invite_id)} data-testid={`invitation-resend-${inv.email}`} className="inline-flex items-center gap-1 rounded-full border border-gold/25 px-3 py-1.5 text-xs text-ivory/70 hover:bg-gold/10"><RefreshCw className="h-3 w-3" /> Resend</button>
                  <button onClick={() => revoke(inv.invite_id)} data-testid={`invitation-revoke-${inv.email}`} className="inline-flex items-center gap-1 rounded-full border border-red-500/40 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10"><Ban className="h-3 w-3" /> Revoke</button>
                </div>
              )}
              {(inv.status === "expired") && (
                <button onClick={() => resend(inv.invite_id)} className="inline-flex items-center gap-1 rounded-full border border-gold/25 px-3 py-1.5 text-xs text-ivory/70 hover:bg-gold/10"><RefreshCw className="h-3 w-3" /> Resend</button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gold/15 bg-obsidian-800" data-testid="members-list">
        <p className="border-b border-gold/10 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-gold">Team Members</p>
        {members.map((m) => (
          <div key={m.membership_id} className="flex flex-wrap items-center justify-between gap-3 border-b border-gold/5 px-5 py-4" data-testid={`member-${m.email}`}>
            <div>
              <p className="flex items-center gap-2 text-sm text-ivory">{m.name || m.email}
                {m.is_primary && <span className="inline-flex items-center gap-1 rounded-full border border-gold/40 px-2 py-0.5 text-[10px] font-semibold text-gold-light" data-testid={`member-primary-${m.email}`}><ShieldCheck className="h-3 w-3" /> Primary Admin</span>}
              </p>
              <p className="text-xs text-ivory/50">{m.email} · {roleLabel(m.role)} · <span className={m.status === "active" ? "text-gold-light" : "text-amber-400"}>{m.status}</span></p>
            </div>
            {m.is_primary ? (
              <span className="text-xs text-ivory/40">Managed by Nanak Chauffeurs</span>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <select value={m.role} onChange={(e) => update(m, { role: e.target.value })} className={sel} data-testid={`member-role-${m.email}`}>
                  {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-ivory/60"><input type="checkbox" checked={!!m.company_notifications} onChange={(e) => update(m, { company_notifications: e.target.checked })} data-testid={`member-notify-${m.email}`} /> Alerts</label>
                <button onClick={() => update(m, { status: m.status === "active" ? "suspended" : "active" })} className="rounded-full border border-gold/25 px-3 py-2 text-xs text-ivory/70 hover:bg-gold/10" data-testid={`member-toggle-${m.email}`}>
                  {m.status === "active" ? "Deactivate" : "Reactivate"}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-ivory/45">Deactivating a member revokes their access immediately. Managing team access never changes reservation data — all reservations stay read-only and are owned by Limo Anywhere. Only Nanak Chauffeurs can appoint or replace the Primary Administrator.</p>
    </div>
  );
}
